import { ExternalLink, Pencil, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { ActionRow } from '@reflex/ui/components/shared/ActionRow';
import { StatusBadge } from './StatusBadge';
import { renderInlineLinks } from '@/lib/inline-links';
import type { SecretStatusItem } from '@/lib/types';

/**
 * Renders the trailing status pill for a secret row. Three states:
 *
 * - `isSet`        → coloured source badge (Set via UI / env / file)
 * - `required` & unset → "Required" tag in warning amber, so the row
 *   reads as an actionable miss rather than just an empty slot.
 * - optional & unset   → no pill at all. The "Add" button on the right
 *   is the only affordance; without the warning-tinted "Not set"
 *   chip the card no longer looks like four alarms when only one of
 *   the LLM provider keys is genuinely required.
 */
function SecretRowStatus({ item }: { item: SecretStatusItem }) {
  if (item.isSet) {
    return <StatusBadge source={item.source} />;
  }
  if (item.required) {
    return (
      <Badge
        variant="secondary"
        className="border-status-warning/30 bg-status-warning/5 text-2xs font-medium text-status-warning"
      >
        Required
      </Badge>
    );
  }
  return null;
}

export function SecretRow({
  item,
  onEdit,
  isPending,
  beforeEditAction,
  addLabel,
}: {
  item: SecretStatusItem;
  onEdit: () => void;
  isPending?: boolean;
  /** Optional action placed after the provider link and before Add/Edit. */
  beforeEditAction?: ReactNode;
  /** Optional replacement label when an unset secret is added. */
  addLabel?: string;
}) {
  return (
    <ActionRow
      title={item.label}
      titleAdornment={<SecretRowStatus item={item} />}
      description={item.description ? renderInlineLinks(item.description) : undefined}
      actions={
        <>
          {item.docsUrl && (
            // Pre-dialog discovery of where to obtain this provider's key.
            // The same URL is surfaced inside `SecretInputDialog` (after the
            // user clicks Add/Edit), but most admins decide whether they
            // *have* a key before opening the dialog — exposing the link on
            // the row itself shortcuts that flow. Rendered as a ghost-style
            // link so it stays subordinate to the primary Add/Edit action
            // (no two competing buttons on the right rail).
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <a
                href={item.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Get ${item.label} from the provider`}
                data-testid={`secret-row-get-key-${item.key}`}
              >
                <ExternalLink className="size-3" aria-hidden="true" /> Get key
              </a>
            </Button>
          )}
          {beforeEditAction}
          <Button variant="outline" size="sm" onClick={onEdit} disabled={isPending}>
            {item.isSet ? (
              <>
                <Pencil className="size-3" /> Edit
              </>
            ) : (
              <>
                <Plus className="size-3" /> {addLabel ?? 'Add'}
              </>
            )}
          </Button>
        </>
      }
    />
  );
}
